import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { pathOr } from 'ramda'
import { RootState } from 'store'

import PartnersRepository from 'services/repositories/PartnersRepository'
import { IPartner, IPartnersQueryPayload } from 'types/partners'
import { IPartnersState } from 'types/slices'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'

const initialState: IPartnersState = {
  status: 'initial',
  partners: [],
  partnersByCurrentCountry: [],
  partnersByCurrentCountryAndBrand: [],
  partner: {
    id: '',
    name: '',
    createdAt: '',
    logoUrl: undefined,
    logoFileName: '',
    logoBucketKey: '',
    country: undefined,
    updatedAt: '',
    deletedAt: null
  }
}

export const types = {
  getAllPartners: 'partners/getAllPartners',
  getPartnersByCountry: 'partners/getPartnersByCountry',
  getPartnerById: 'partners/getPartnerById',
  getPartnersByCurrentCountryAndBrand:
    'partners/getPartnersByCurrentCountryAndBrand'
} as const

export const getAllPartners = createAsyncThunk(
  types.getAllPartners,
  async (query: IPartnersQueryPayload, { rejectWithValue }) => {
    try {
      const response = await PartnersRepository.getAllPartners({ query })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getPartnersByCountry = createAsyncThunk(
  types.getPartnersByCountry,
  async (country: string, { rejectWithValue }) => {
    try {
      const response = await PartnersRepository.getPartnersByCountry(country)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getPartnersByCurrentCountryAndBrand = createAsyncThunk(
  types.getPartnersByCurrentCountryAndBrand,
  async (
    payload: { country: string; brandName: string; landingPage?: boolean },
    { rejectWithValue }
  ) => {
    const { country, brandName, landingPage } = payload

    try {
      const response =
        await PartnersRepository.getPartnersByCurrentCountryAndBrand(
          country,
          brandName,
          landingPage
        )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getPartnerById = createAsyncThunk(
  types.getPartnerById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnersRepository.getPartnerById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const partnersSlice = createSlice({
  name: 'partners',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllPartners.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAllPartners.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.partners = action.payload
      })
      .addCase(getAllPartners.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getPartnersByCountry.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getPartnersByCountry.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.partnersByCurrentCountry = action.payload
      })
      .addCase(getPartnersByCountry.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getPartnersByCurrentCountryAndBrand.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(
        getPartnersByCurrentCountryAndBrand.fulfilled,
        (state, action) => {
          state.status = LoadingStatus.loaded
          state.partnersByCurrentCountryAndBrand = action.payload
        }
      )
      .addCase(getPartnersByCurrentCountryAndBrand.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getPartnerById.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getPartnerById.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.partner = action.payload
      })
      .addCase(getPartnerById.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
  }
})

export const selectPartners = (state: RootState) => state.partners
export const selectStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectPartners, partnersSlice => partnersSlice.status)

export const selectAllPartners: (state: RootState) => IPartner[] =
  createSelector(selectPartners, pathOr([], ['partners']))

export const selectPartnersByCurrentCountry: (state: RootState) => IPartner[] =
  createSelector(selectPartners, pathOr([], ['partnersByCurrentCountry']))

export const selectPartnersByCurrentCountryAndBrand: (
  state: RootState
) => IPartner[] = createSelector(
  selectPartners,
  pathOr([], ['partnersByCurrentCountryAndBrand'])
)

export const selectPartner: (state: RootState) => IPartner = createSelector(
  selectPartners,
  partnersSlice => partnersSlice.partner
)

export default partnersSlice.reducer
